/**
 * Wage hub — รวมเรื่อง "ค่าจ้าง" (วิเคราะห์ + master data ค่าจ้าง + ปลายปี).
 *
 * Tabs:
 *   - วิเคราะห์      → PayrollAnalysisContent
 *   - ตั้งค่าคอม     → HRCommissionPackagesContent  (เฉพาะ packages — runs คงอยู่ใน cycle hub)
 *   - ปลายปี        → OwnerYearEndContent
 *   - เงินเพิ่ม      → HRAllowanceMasterContent
 *   - เงินหัก       → OwnerDeductionsContent
 *
 * "ทำเงินเดือน" จริงๆ ยังอยู่ใน /owner/cycle#payroll (รอบเงินเดือน hub)
 * — Wage hub โฟกัสที่ค่าจ้าง master + analysis ไม่ใช่ workflow ปิดงวด.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { HubHeader, PillTabs, roleLabelTh } from '../../components/layout/HubHeader';
import { useAuthStore } from '../../lib/store';
import { useMyCapabilities } from '../../hooks';
import { PayrollAnalysisContent } from './PayrollAnalysis';
import { HRCommissionPackagesContent } from '../hr/CommissionPackages';
import { OwnerYearEndContent } from './YearEnd';
import { HRAllowanceMasterContent } from '../hr/AllowanceMaster';
import { OwnerDeductionsContent } from './Deductions';

type WageTab = 'analysis' | 'commission' | 'year-end' | 'allowance' | 'deduction';
const VALID_TABS: WageTab[] = ['analysis', 'commission', 'year-end', 'allowance', 'deduction'];

export function Wage() {
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const { data: caps } = useMyCapabilities();
  // แท็บผูกกับ "สิทธิ์" ให้ตรงกับ backend: วิเคราะห์ค่าจ้าง = edit_salary,
  // ตั้งค่าเงินหัก = config_pay (เดิมล็อกไว้ที่ตำแหน่งเจ้าของ → HR เห็นเมนูแต่กดแล้ว 403)
  const canAnalyze = role === 'OWNER' || (caps?.includes('edit_salary') ?? false);
  const canConfigPay = role === 'OWNER' || (caps?.includes('config_pay') ?? false);

  const [tab, setTab] = useState<WageTab>(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '') as WageTab;
    return VALID_TABS.includes(h) ? h : 'analysis';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${tab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [tab]);
  // sync tab จาก #hash เมื่อ navigate ผ่าน sidebar (เมนูย่อย)
  const __loc = useLocation();
  useEffect(() => {
    const h = __loc.hash.replace('#', '') as WageTab;
    if (VALID_TABS.includes(h)) setTab(h);
  }, [__loc.hash]);


  // Order: วิเคราะห์ → ปลายปี → setup masters → คอม (setup ครั้งเดียว)
  const tabs: Array<{ key: WageTab; label: string; visible: boolean }> = [
    { key: 'analysis', label: 'วิเคราะห์ค่าจ้าง', visible: canAnalyze },
    { key: 'year-end', label: 'สรุปปลายปี / 50 ทวิ', visible: true },
    { key: 'allowance', label: 'ตั้งค่าเงินเพิ่ม', visible: true },
    { key: 'deduction', label: 'ตั้งค่าเงินหัก', visible: canConfigPay },
    { key: 'commission', label: 'ตั้งค่าคอมมิชชั่น', visible: true },
  ];
  const visible = tabs.filter((t) => t.visible);
  const active = visible.some((t) => t.key === tab) ? tab : visible[0]?.key ?? 'year-end';

  return (
    <AdminLayout title="ค่าจ้าง">
      <HubHeader
        section="ค่าจ้าง"
        title={visible.find((t) => t.key === active)?.label ?? 'ค่าจ้าง'}
        roleLabel={roleLabelTh(role)}
      />
      <PillTabs tabs={visible} active={active} onChange={setTab} />

      {active === 'analysis' && <PayrollAnalysisContent />}
      {active === 'commission' && <HRCommissionPackagesContent />}
      {active === 'year-end' && <OwnerYearEndContent />}
      {active === 'allowance' && <HRAllowanceMasterContent />}
      {active === 'deduction' && <OwnerDeductionsContent />}
    </AdminLayout>
  );
}
